import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getForm, updateForm, Form, Field, toggleFormStatus, cloneForm, deleteForm, exportFormData } from '../utils/api';
import { Button, Card, CardHeader, CardTitle, CardContent, Input, Textarea, Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose, DialogDescription } from '../components/ui';
import { PlusIcon, TrashIcon, GripVerticalIcon, TextIcon, EmailIcon, UrlIcon, NumberIcon, TextareaIcon, SelectIcon, SaveIcon, ArrowLeftIcon, ChevronDownIcon, ChevronUpIcon, MoreHorizontalIcon, CopyIcon, CodeIcon, PublishIcon, UnpublishIcon, ExportIcon } from '../components/Icons';
import { generateEmbedUrl, generateFormsUrl } from '../utils';

type FieldType = 'text' | 'email' | 'url' | 'number' | 'textarea' | 'select';

interface FormWithFields extends Form {
  fields: Field[];
}

const fieldTypeIcons: Record<FieldType, React.FC<React.SVGProps<SVGSVGElement>>> = {
  text: TextIcon,
  email: EmailIcon,
  url: UrlIcon,
  number: NumberIcon,
  textarea: TextareaIcon,
  select: SelectIcon,
};

const fieldTypeLabels: Record<FieldType, string> = {
  text: 'Text Input',
  email: 'Email',
  url: 'URL',
  number: 'Number',
  textarea: 'Textarea',
  select: 'Select',
};

const FormBuilderPage: React.FC = () => {
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const [form, setForm] = useState<FormWithFields | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedField, setExpandedField] = useState<string | null>(null);
  const [deleteConfirmField, setDeleteConfirmField] = useState<number | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const fetchForm = useCallback(async () => {
    if (!formId) return;
    setLoading(true);
    try {
      const formData = await getForm(formId) as FormWithFields;
      if (formData.fields) {
        formData.fields.sort((a, b) => a.order_index - b.order_index);
      } else {
        formData.fields = [];
      }
      setForm(formData);
    } catch (error) {
      console.error('Failed to fetch form:', error);
    } finally {
      setLoading(false);
    }
  }, [formId]);

  useEffect(() => {
    fetchForm();
  }, [fetchForm]);

  const addField = (type: FieldType) => {
    if (!form) return;
    
    const newField: Field = {
      id: `new-${Date.now()}`,
      form_id: form.id,
      type,
      label: `New ${fieldTypeLabels[type]}`,
      placeholder: '',
      required: false,
      validation_regex: '',
      validation_message: '',
      disabled_domains: [],
      field_options: type === 'select' ? ['Option 1', 'Option 2'] : [],
      order_index: form.fields.length,
    };

    setForm({
      ...form,
      fields: [...form.fields, newField],
    });
    
    // Auto-expand the new field
    setExpandedField(newField.id);
  };

  const updateField = (index: number, updates: Partial<Field>) => {
    if (!form) return;
    
    const newFields = [...form.fields];
    newFields[index] = { ...newFields[index], ...updates };
    setForm({ ...form, fields: newFields });
  };

  const removeField = (index: number) => {
    if (!form) return;
    
    const newFields = form.fields.filter((_, i) => i !== index);
    // Update order_index for remaining fields
    newFields.forEach((field, idx) => {
      field.order_index = idx;
    });
    setForm({ ...form, fields: newFields });
    setExpandedField(null);
    setDeleteConfirmField(null);
  };

  const moveField = (fromIndex: number, toIndex: number) => {
    if (!form?.fields) return;
    
    const newFields = [...form.fields];
    const [moved] = newFields.splice(fromIndex, 1);
    newFields.splice(toIndex, 0, moved);
    
    // Update order_index for all fields
    newFields.forEach((field, index) => {
      field.order_index = index;
    });
    
    setForm({ ...form, fields: newFields });
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.setData('text/plain', index.toString());
    e.dataTransfer.effectAllowed = 'move';
    
    // Add some visual feedback
    if (e.currentTarget) {
      e.currentTarget.style.opacity = '0.5';
    }
  };

  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    setDraggedIndex(null);
    setDragOverIndex(null);
    
    // Reset visual feedback
    if (e.currentTarget) {
      e.currentTarget.style.opacity = '1';
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    
    if (dragIndex !== dropIndex && !isNaN(dragIndex)) {
      moveField(dragIndex, dropIndex);
    }
    
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleSave = async () => {
    if (!form || !formId) return;
    setSaving(true);
    try {
      await updateForm(formId, form);
      navigate('/');
    } catch (error) {
      console.error('Failed to save form:', error);
    } finally {
      setSaving(false);
    }
  };

  const updateFieldOptions = (fieldIndex: number, options: string[]) => {
    updateField(fieldIndex, { field_options: options });
  };

  const toggleFieldExpansion = (fieldId: string) => {
    setExpandedField(expandedField === fieldId ? null : fieldId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading form builder...</p>
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-xl font-semibold mb-2">Form not found</p>
          <p className="text-muted-foreground mb-4">The form you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/')}>
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Forms
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-background sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={() => navigate('/')}>
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-semibold">{form.name}</h1>
                {form.description && (
                  <p className="text-sm text-muted-foreground">{form.description}</p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {/* Form Actions Menu */}
              <div className="relative">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm">
                      <MoreHorizontalIcon className="h-4 w-4 mr-2" />
                      Actions
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Form Actions</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2">
                      <Button 
                        variant="ghost" 
                        className="w-full justify-start"
                        onClick={async () => {
                          try {
                            await toggleFormStatus(formId!);
                            await fetchForm(); // Refresh form data
                          } catch (error) {
                            console.error('Failed to toggle status:', error);
                          }
                        }}
                      >
                        {form?.status === 'published' ? (
                          <>
                            <UnpublishIcon className="h-4 w-4 mr-2" />
                            Make Private
                          </>
                        ) : (
                          <>
                            <PublishIcon className="h-4 w-4 mr-2" />
                            Publish Form
                          </>
                        )}
                      </Button>
                      <Button 
                        variant="ghost" 
                        className="w-full justify-start"
                        onClick={async () => {
                          try {
                            const result = await cloneForm(formId!);
                            navigate(`/form/${result.newFormId}/edit`);
                          } catch (error) {
                            console.error('Failed to clone form:', error);
                          }
                        }}
                      >
                        <CopyIcon className="h-4 w-4 mr-2" />
                        Clone Form
                      </Button>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="ghost" className="w-full justify-start">
                            <CodeIcon className="h-4 w-4 mr-2" />
                            Embed URL
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Embed Form</DialogTitle>
                            <DialogDescription>Copy this URL to share or embed the form.</DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div>
                              <label className="text-sm font-medium mb-2 block">Direct Link</label>
                              <Textarea 
                                readOnly 
                                value={generateEmbedUrl(formId!)}
                                rows={2} 
                                className="font-mono text-sm" 
                              />
                            </div>
                          </div>
                          <DialogFooter>
                            <Button onClick={() => {
                              const embedUrl = generateEmbedUrl(formId!);
                              navigator.clipboard.writeText(embedUrl);
                              alert('Embed URL copied to clipboard!');
                            }}>
                              Copy URL
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                      <Button 
                        variant="ghost" 
                        className="w-full justify-start"
                        onClick={async () => {
                          try {
                            const data = await exportFormData(formId!);
                            const jsonContent = JSON.stringify(data, null, 2);
                            const blob = new Blob([jsonContent], { type: 'application/json' });
                            const url = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `${data.form.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_export.json`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            URL.revokeObjectURL(url);
                          } catch (error) {
                            console.error('Failed to export:', error);
                          }
                        }}
                      >
                        <ExportIcon className="h-4 w-4 mr-2" />
                        Export Data
                      </Button>
                      <div className="h-px bg-border my-2" />
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="ghost" className="w-full justify-start text-destructive hover:text-destructive">
                            <TrashIcon className="h-4 w-4 mr-2" />
                            Delete Form
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Delete Form</DialogTitle>
                          </DialogHeader>
                          <p className="text-muted-foreground">
                            Are you sure you want to delete this form and all its submissions? This action cannot be undone.
                          </p>
                          <DialogFooter>
                            <DialogClose asChild>
                              <Button variant="outline">Cancel</Button>
                            </DialogClose>
                            <Button 
                              variant="destructive"
                              onClick={async () => {
                                try {
                                  await deleteForm(formId!);
                                  navigate('/');
                                } catch (error) {
                                  console.error('Failed to delete form:', error);
                                }
                              }}
                            >
                              Delete Form
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              <Button variant="outline" onClick={() => navigate('/')}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                <SaveIcon className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Form'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Field Types Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24 shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center space-x-2 text-lg">
                  <div className="flex items-center justify-center w-8 h-8 rounded-md bg-primary/10">
                    <PlusIcon className="h-5 w-5 text-primary" />
                  </div>
                  <span>Add Fields</span>
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                  Click to add field types to your form
                </p>
              </CardHeader>
              <CardContent className="space-y-2 pt-0">
                {(Object.keys(fieldTypeLabels) as FieldType[]).map((type) => {
                  const Icon = fieldTypeIcons[type];
                  return (
                    <Button
                      key={type}
                      variant="ghost"
                      className="w-full justify-start hover:bg-accent hover:text-accent-foreground h-12 text-left"
                      onClick={() => addField(type)}
                    >
                      <div className="flex items-center justify-center w-8 h-8 rounded-md bg-primary/10 mr-3">
                        <Icon className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-sm">{fieldTypeLabels[type]}</div>
                        <div className="text-xs text-muted-foreground">
                          {type === 'text' && 'Single line text input'}
                          {type === 'email' && 'Email address field'}
                          {type === 'url' && 'Website URL field'}
                          {type === 'number' && 'Numeric input field'}
                          {type === 'textarea' && 'Multi-line text area'}
                          {type === 'select' && 'Dropdown selection'}
                        </div>
                      </div>
                    </Button>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Form Builder */}
          <div className="lg:col-span-3">
            <Card>
              <CardHeader>
                <CardTitle>Form Fields</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Drag and drop fields to reorder them. Click on a field to configure its properties.
                </p>
              </CardHeader>
              <CardContent>
                {form.fields && form.fields.length > 0 ? (
                  <div className="space-y-3">
                    {form.fields.map((field, index) => {
                      const isExpanded = expandedField === field.id;
                      const Icon = fieldTypeIcons[field.type];
                      const isDragging = draggedIndex === index;
                      const isDragOver = dragOverIndex === index;
                      
                      return (
                        <div
                          key={field.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, index)}
                          onDragEnd={handleDragEnd}
                          onDragOver={(e) => handleDragOver(e, index)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, index)}
                          className={`transition-all duration-200 ${
                            isDragOver ? 'transform scale-[1.02] shadow-lg' : ''
                          } ${isDragging ? 'opacity-50 rotate-1' : ''}`}
                        >
                          <Card
                            className={`transition-all duration-200 border-2 ${
                              isExpanded 
                                ? 'ring-2 ring-primary/20 border-primary/30 shadow-md bg-primary/5' 
                                : 'border-border hover:border-primary/40 hover:shadow-sm'
                            } ${
                              isDragOver ? 'border-primary bg-primary/10 shadow-lg' : ''
                            } ${
                              isDragging ? 'border-dashed border-primary/50' : ''
                            }`}
                          >
                            {/* Field Header */}
                            <div className="flex items-center justify-between p-4 cursor-grab active:cursor-grabbing hover:bg-accent/20 transition-colors border-b border-border/50">
                              <div className="flex items-center space-x-3">
                                <div className="flex items-center justify-center w-8 h-8 rounded-md bg-muted hover:bg-primary/10 transition-colors">
                                  <GripVerticalIcon className="h-4 w-4 text-muted-foreground hover:text-primary transition-colors" />
                                </div>
                                <div className="flex items-center justify-center w-8 h-8 rounded-md bg-primary/10">
                                  <Icon className="h-4 w-4 text-primary" />
                                </div>
                                <div className="flex-1">
                                  <div className="font-medium text-sm text-foreground">{field.label}</div>
                                  <div className="text-xs text-muted-foreground flex items-center space-x-2">
                                    <span>{fieldTypeLabels[field.type]}</span>
                                    {field.required && (
                                      <>
                                        <span>•</span>
                                        <span className="text-destructive font-medium">Required</span>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleFieldExpansion(field.id);
                                  }}
                                  className="h-8 w-8 p-0 hover:bg-accent rounded-md transition-colors"
                                  title={isExpanded ? "Collapse field" : "Expand field"}
                                >
                                  {isExpanded ? (
                                    <ChevronUpIcon className="h-4 w-4" />
                                  ) : (
                                    <ChevronDownIcon className="h-4 w-4" />
                                  )}
                                </Button>
                                <Dialog 
                                  open={deleteConfirmField === index} 
                                  onOpenChange={(open) => setDeleteConfirmField(open ? index : null)}
                                >
                                  <DialogTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => e.stopPropagation()}
                                      className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10 rounded-md transition-colors"
                                      title="Delete field"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent>
                                    <DialogHeader>
                                      <DialogTitle>Delete Field</DialogTitle>
                                    </DialogHeader>
                                    <p className="text-muted-foreground">
                                      Are you sure you want to delete the field <strong>"{field.label}"</strong>? This action cannot be undone.
                                    </p>
                                    <DialogFooter>
                                      <DialogClose asChild>
                                        <Button variant="outline">Cancel</Button>
                                      </DialogClose>
                                      <Button
                                        variant="destructive"
                                        onClick={() => removeField(index)}
                                      >
                                        Delete Field
                                      </Button>
                                    </DialogFooter>
                                  </DialogContent>
                                </Dialog>
                              </div>
                            </div>

                            {/* Field Configuration (Collapsible) */}
                            {isExpanded && (
                              <div className="p-6 space-y-6 bg-accent/3 border-t">
                                {/* Basic Configuration */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                  <div className="space-y-2">
                                    <label className="text-sm font-semibold text-foreground">Field Label</label>
                                    <Input
                                      value={field.label}
                                      onChange={(e) => updateField(index, { label: e.target.value })}
                                      placeholder="Enter field label"
                                      className="w-full focus:ring-2 focus:ring-primary/20"
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <label className="text-sm font-semibold text-foreground">Placeholder Text</label>
                                    <Input
                                      value={field.placeholder || ''}
                                      onChange={(e) => updateField(index, { placeholder: e.target.value })}
                                      placeholder={
                                        field.type === 'url' ? 'https://example.com' :
                                        field.type === 'email' ? 'user@example.com' :
                                        field.type === 'number' ? '123' :
                                        'Enter placeholder text'
                                      }
                                      className="w-full focus:ring-2 focus:ring-primary/20"
                                    />
                                    {field.type === 'url' && (
                                      <p className="text-xs text-muted-foreground">
                                        URLs will be automatically validated. Users can enter with or without https://
                                      </p>
                                    )}
                                    {field.type === 'email' && (
                                      <p className="text-xs text-muted-foreground">
                                        Email addresses will be validated for proper format (user@domain.com)
                                      </p>
                                    )}
                                    {field.type === 'number' && (
                                      <p className="text-xs text-muted-foreground">
                                        Only numeric values will be accepted
                                      </p>
                                    )}
                                  </div>
                                </div>

                                {/* Select Field Options */}
                                {field.type === 'select' && (
                                  <div className="space-y-4">
                                    <label className="text-sm font-semibold text-foreground">Select Options</label>
                                    <div className="space-y-3">
                                      {field.field_options?.map((option, optionIndex) => (
                                        <div key={optionIndex} className="flex gap-3 items-center">
                                          <div className="flex-1">
                                            <Input
                                              value={option}
                                              onChange={(e) => {
                                                const newOptions = [...(field.field_options || [])];
                                                newOptions[optionIndex] = e.target.value;
                                                updateFieldOptions(index, newOptions);
                                              }}
                                              placeholder={`Option ${optionIndex + 1}`}
                                              className="w-full focus:ring-2 focus:ring-primary/20"
                                            />
                                          </div>
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                              const newOptions = field.field_options?.filter((_, i) => i !== optionIndex) || [];
                                              updateFieldOptions(index, newOptions);
                                            }}
                                            className="h-10 w-10 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                            title="Remove option"
                                          >
                                            <TrashIcon className="h-4 w-4" />
                                          </Button>
                                        </div>
                                      ))}
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                          const newOptions = [...(field.field_options || []), `Option ${(field.field_options?.length || 0) + 1}`];
                                          updateFieldOptions(index, newOptions);
                                        }}
                                        className="w-full border-dashed border-2 hover:border-primary hover:bg-primary/5"
                                      >
                                        <PlusIcon className="h-4 w-4 mr-2" />
                                        Add Option
                                      </Button>
                                    </div>
                                  </div>
                                )}

                                {/* URL Field Suppressed Domains */}
                                {field.type === 'url' && (
                                  <div className="space-y-4">
                                    <label className="text-sm font-semibold text-foreground">Suppressed Domains</label>
                                    <p className="text-xs text-muted-foreground">
                                      Block submissions from specific domains. Enter just the domain name (e.g., "example.com", "spam-site.org")
                                    </p>
                                    <div className="space-y-3">
                                      {field.disabled_domains?.map((domain, domainIndex) => (
                                        <div key={domainIndex} className="flex gap-3 items-center">
                                          <div className="flex-1">
                                            <Input
                                              value={domain}
                                              onChange={(e) => {
                                                const newDomains = [...(field.disabled_domains || [])];
                                                newDomains[domainIndex] = e.target.value.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '');
                                                updateField(index, { disabled_domains: newDomains });
                                              }}
                                              placeholder="example.com"
                                              className="w-full focus:ring-2 focus:ring-primary/20"
                                            />
                                          </div>
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                              const newDomains = field.disabled_domains?.filter((_, i) => i !== domainIndex) || [];
                                              updateField(index, { disabled_domains: newDomains });
                                            }}
                                            className="h-10 w-10 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                            title="Remove domain"
                                          >
                                            <TrashIcon className="h-4 w-4" />
                                          </Button>
                                        </div>
                                      ))}
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                          const newDomains = [...(field.disabled_domains || []), ''];
                                          updateField(index, { disabled_domains: newDomains });
                                        }}
                                        className="w-full border-dashed border-2 hover:border-primary hover:bg-primary/5"
                                      >
                                        <PlusIcon className="h-4 w-4 mr-2" />
                                        Add Domain to Block
                                      </Button>
                                    </div>
                                  </div>
                                )}

                                {/* Validation and Requirements */}
                                <div className="space-y-4 pt-4 border-t border-border/50">
                                  <div className="flex items-center space-x-3">
                                    <input
                                      type="checkbox"
                                      id={`required-${field.id}`}
                                      checked={field.required}
                                      onChange={(e) => updateField(index, { required: e.target.checked })}
                                      className="rounded border-border text-primary focus:ring-primary focus:ring-2 focus:ring-offset-0 h-4 w-4"
                                    />
                                    <label
                                      htmlFor={`required-${field.id}`}
                                      className="text-sm font-semibold text-foreground cursor-pointer"
                                    >
                                      Required field
                                    </label>
                                  </div>
                                  
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                      <label className="text-sm font-semibold text-foreground">Validation Pattern</label>
                                      <Input
                                        value={field.validation_regex || ''}
                                        onChange={(e) => updateField(index, { validation_regex: e.target.value })}
                                        placeholder="^[a-zA-Z]+$ (optional)"
                                        className="w-full focus:ring-2 focus:ring-primary/20 font-mono text-xs"
                                      />
                                      <p className="text-xs text-muted-foreground">Regular expression for validation</p>
                                    </div>
                                    <div className="space-y-2">
                                      <label className="text-sm font-semibold text-foreground">Error Message</label>
                                      <Input
                                        value={field.validation_message || ''}
                                        onChange={(e) => updateField(index, { validation_message: e.target.value })}
                                        placeholder="Custom error message"
                                        className="w-full focus:ring-2 focus:ring-primary/20"
                                      />
                                      <p className="text-xs text-muted-foreground">Message shown when validation fails</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </Card>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="mx-auto w-24 h-24 bg-muted rounded-full flex items-center justify-center mb-4">
                      <PlusIcon className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">No fields added yet</h3>
                    <p className="text-muted-foreground mb-6">
                      Start building your form by adding fields from the sidebar.
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => addField('text')}
                    >
                      <TextIcon className="h-4 w-4 mr-2" />
                      Add your first field
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormBuilderPage;
